'use client';

import React from 'react';
import AppLayout from '@/components/AppLayout';
import HealthScoreCard from './components/HealthScoreCard';
import KpiGrid from './components/KpiGrid';
import ThroughputChart from './components/ThroughputChart';
import LatencyChart from './components/LatencyChart';
import NodeStatusGrid from './components/NodeStatusGrid';
import AlertFeed from './components/AlertFeed';
import AutomationJobList from './components/AutomationJobList';
import RegionHealthTable from './components/RegionHealthTable';
import AnomalyDetectionPanel from './components/AnomalyDetectionPanel';
import { useDashboardRealtime } from '@/hooks/useDashboardRealtime';
import { createClient } from '@/lib/supabase/client';

export default function NetworkOperationsDashboard() {
  const {
    health,
    kpis,
    alerts,
    nodes,
    regions,
    throughput,
    latency,
    loading,
    lastUpdated,
    suppressCount,
  } = useDashboardRealtime();

  const handleDismissAlert = async (id: string) => {
    const supabase = createClient();
    await supabase.from('network_alerts').update({ dismissed: true }).eq('id', id);
  };

  return (
    <AppLayout>
      <div className="p-6 xl:p-8 2xl:p-10 max-w-screen-2xl mx-auto space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Network Operations</h1>
            <p className="text-sm text-muted mt-0.5">
              Real-time telemetry · Nablix Air · Addis Ababa, Ethiopia
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="flex items-center gap-1.5 text-[10px] text-success font-mono">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                Live · {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
              </span>
            )}
            <select className="text-xs bg-surface-raised border border-border rounded-lg px-3 py-1.5 text-foreground-muted focus:outline-none focus:border-primary/50">
              <option>Last 3 hours</option>
              <option>Last 6 hours</option>
              <option>Last 24 hours</option>
              <option>Last 7 days</option>
            </select>
            <button className="text-xs bg-primary/15 text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/25 transition-colors font-medium">
              Export Report
            </button>
          </div>
        </div>

        {/* Row 1: Health score hero + KPI grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
          <HealthScoreCard health={health} loading={loading} lastUpdated={lastUpdated} />
          <div className="md:col-span-3">
            <KpiGrid kpis={kpis} loading={loading} />
          </div>
        </div>

        {/* Row 2: Throughput + Latency charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-4">
          <ThroughputChart data={throughput} loading={loading} />
          <LatencyChart data={latency} loading={loading} />
        </div>

        {/* Row 3: Node grid + Alert feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <NodeStatusGrid nodes={nodes} loading={loading} />
          </div>
          <AlertFeed
            alerts={alerts}
            suppressCount={suppressCount}
            loading={loading}
            onDismiss={handleDismissAlert}
          />
        </div>

        {/* Row 4: Automation jobs + Region health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-4">
          <AutomationJobList />
          <RegionHealthTable regions={regions} loading={loading} />
        </div>

        {/* Row 5: AI Anomaly Detection */}
        <AnomalyDetectionPanel
          throughput={throughput}
          latency={latency}
          regions={regions}
          loading={loading}
        />
      </div>
    </AppLayout>
  );
}